/**
 * Users API Module
 * Методы для управления пользователями: CRUD, регистрация, проверка username
 * 
 * @typedef {import('./types').UserDto} UserDto
 * @typedef {import('./types').CreateUserAdminRequest} CreateUserAdminRequest
 * @typedef {import('./types').CreateUserAdminResponse} CreateUserAdminResponse
 * @typedef {import('./types').UpdateUserRequest} UpdateUserRequest
 * @typedef {import('./types').ChangeRoleRequest} ChangeRoleRequest
 * @typedef {import('./types').UsernameCheckResponse} UsernameCheckResponse
 * @typedef {import('./types').DeactivateUserResponse} DeactivateUserResponse
 * @typedef {import('./types').RegisterRequest} RegisterRequest
 * @typedef {import('./types').RegisterResponse} RegisterResponse
 */

import { authFetch } from '../authClient';

/**
 * Базовый URL для Auth API
 * @returns {string}
 */
function getBaseUrl() {
  return import.meta.env.VITE_AUTH_API_URL ?? '';
}

/**
 * POST /api/auth/register
 * Регистрация нового пользователя (публичный endpoint - без токена)
 * @param {RegisterRequest} data
 * @returns {Promise<RegisterResponse>}
 */
export async function register(data) {
  const response = await fetch(`${getBaseUrl()}/api/auth/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || 'Registration failed');
    error.status = response.status;
    error.data = errorData;
    throw error;
  }

  return response.json();
}

/**
 * GET /api/auth/me
 * Получить данные текущего авторизованного пользователя
 * @returns {Promise<UserDto>}
 */
export async function getMe() {
  return authFetch('/api/auth/me');
}

/**
 * @typedef {Object} GetUsersParams
 * @property {'user'|'admin'} [role]            - Фильтр по роли
 * @property {boolean} [isActive]               - Фильтр по статусу
 * @property {string} [registeredFrom]          - ISO 8601 UTC
 * @property {string} [registeredTo]            - ISO 8601 UTC
 * @property {string} [search]                  - Поиск по name/surname/username
 * @property {number} [page=1]
 * @property {number} [pageSize=20]             - Max 100
 * @property {'createdAt'|'name'|'surname'|'role'|'lastLogin'} [sortBy='createdAt']
 * @property {'asc'|'desc'} [sortDirection='desc']
 */

/**
 * @typedef {Object} UserListItemDto
 * @property {number} userId
 * @property {string} name
 * @property {string} surname
 * @property {string} username
 * @property {string} email
 * @property {'user'|'admin'} role
 * @property {boolean} isActive
 * @property {string|null} lastLoginAt        - ISO 8601 UTC
 * @property {string} createdAt               - ISO 8601 UTC
 */

/**
 * @typedef {Object} GetUsersResponse
 * @property {UserListItemDto[]} items
 * @property {number} total
 * @property {number} page
 * @property {number} pageSize
 * @property {number} totalPages
 */

/**
 * GET /api/auth/users
 * Получить постраничный список пользователей (admin only).
 * Поддерживает фильтрацию по роли/статусу/датам регистрации, поиск, сортировку.
 *
 * @param {GetUsersParams} [params]
 * @returns {Promise<GetUsersResponse>}
 */
export async function getUsers(params = {}) {
  const query = new URLSearchParams();

  if (params.role) query.set('role', params.role);
  if (params.isActive !== undefined && params.isActive !== null && params.isActive !== '') {
    query.set('isActive', String(Boolean(params.isActive)));
  }
  if (params.registeredFrom) query.set('registeredFrom', params.registeredFrom);
  if (params.registeredTo) query.set('registeredTo', params.registeredTo);
  if (params.search) query.set('search', params.search);
  if (params.page) query.set('page', String(params.page));
  if (params.pageSize) query.set('pageSize', String(params.pageSize));
  if (params.sortBy) query.set('sortBy', params.sortBy);
  if (params.sortDirection) query.set('sortDirection', params.sortDirection);

  const queryStr = query.toString();
  return authFetch(`/api/auth/users${queryStr ? `?${queryStr}` : ''}`);
}

/**
 * POST /api/auth/users
 * Создать пользователя (admin only)
 * @param {CreateUserAdminRequest} data
 * @returns {Promise<CreateUserAdminResponse>}
 */
export async function createUserAdmin(data) {
  return authFetch('/api/auth/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * PUT /api/auth/users/me
 * Обновить данные текущего пользователя
 * @param {UpdateUserRequest} data
 * @returns {Promise<UserDto>}
 */
export async function updateMe(data) {
  return authFetch('/api/auth/users/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * PUT /api/auth/users/{id}
 * Обновить пользователя (admin only)
 * @param {number} userId
 * @param {UpdateUserRequest} data
 * @returns {Promise<UserDto>}
 */
export async function updateUser(userId, data) {
  return authFetch(`/api/auth/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * PATCH /api/auth/users/{id}/role
 * Изменить роль пользователя (admin only)
 * @param {number} userId
 * @param {ChangeRoleRequest} data
 * @returns {Promise<UserDto>}
 */
export async function changeUserRole(userId, data) {
  return authFetch(`/api/auth/users/${userId}/role`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * DELETE /api/auth/users/{id}
 * Деактивировать пользователя (admin only)
 * @param {number} userId
 * @returns {Promise<DeactivateUserResponse>}
 */
export async function deactivateUser(userId) {
  return authFetch(`/api/auth/users/${userId}`, {
    method: 'DELETE',
  });
}

/**
 * GET /api/auth/username-check
 * Проверить доступность username
 * @param {string} username
 * @returns {Promise<UsernameCheckResponse>}
 */
export async function checkUsernameAvailability(username) {
  return authFetch('/api/auth/username-check', {
    params: { username },
  });
}
